import type { AnalyticalVariableListItem } from '@verantos-dev/feature-analytical-variables';

/**
 * Why a library variable cannot be added as a criterion to a cohort whose index date is
 * conditional on the other criteria ("all other criteria met").
 *
 * - `nonBooleanCriterion`: criteria evaluated at each candidate index date must be boolean.
 * - `missingIndexDateColumn`: the criterion depends on the index date but its output
 *   carries no index date column, so it cannot be evaluated per candidate.
 */
export type AvIneligibilityReason = 'nonBooleanCriterion' | 'missingIndexDateColumn';

export type DependentCriteriaMode = 'all' | 'none' | null | undefined;

/**
 * Mirrors `check_criterion_for_conditional_index` in
 * `cohort_service/cohort_index_validation.py`. Keep the two in sync; the server-side gate at
 * bind time is the authority, this only pre-disables a row.
 *
 * Only the criterion role is mirrored: the index-date picker lists date variables, which
 * write the absolute occurrence, and a boolean index with a mode is set through the
 * Research Assistant only.
 *
 * A shape flag the server did not send (`undefined`/`null`) is unknown, not false, so it
 * never disables a row.
 *
 * @param item - Library list item.
 * @param dependentCriteriaMode - The cohort's index rule; the rule applies only for `'all'`.
 * @returns A reason code, or `undefined` when the variable is usable.
 */
export function getAvIneligibilityReason(
  item: AnalyticalVariableListItem,
  dependentCriteriaMode: DependentCriteriaMode
): AvIneligibilityReason | undefined {
  if (dependentCriteriaMode !== 'all') return undefined;
  if (item.format.toLowerCase() !== 'boolean') return 'nonBooleanCriterion';
  if (item.indexDependent === true && item.hasIndexDateColumn === false) {
    return 'missingIndexDateColumn';
  }
  return undefined;
}
